import { useEffect, useRef, useState, FormEvent } from "react";
import { useTranslation } from "react-i18next";

export default function ContactSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    const message = data.get("message") || "";
    if (message.length > 500) return;
    setStatus("sending");
    try {
      const res = await fetch("https://readdy.ai/api/form/d73b2nklqq77n94m7feg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const contactItems = [
    { icon: "ri-map-pin-line", key: "contact.address", href: undefined },
    { icon: "ri-mail-line", key: "contact.emailLabel", href: "mailto:info@eunextgrants.md" },
    { icon: "ri-time-line", key: "contact.hours", href: undefined },
  ];

  return (
    <section id="contact" className="bg-white py-20 md:py-28" ref={ref}>
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left — info */}
          <div
            className={`transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <p className="text-xs uppercase tracking-widest text-gold font-medium mb-4">
              {t("contact.label")}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-navy leading-snug mb-6">
              {t("contact.title")}
            </h2>
            <hr className="border-cool-gray-200 mb-6 w-16" />
            <p className="text-navy/65 text-base leading-relaxed mb-10">
              {t("contact.p")}
            </p>

            <ul className="space-y-5">
              {contactItems.map((item) => (
                <li key={item.key} className="flex items-start gap-4">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${item.icon} text-gold text-lg`} />
                  </span>
                  {item.href ? (
                    <a href={item.href} className="text-sm text-navy/70 hover:text-navy transition-colors">
                      {t(item.key)}
                    </a>
                  ) : (
                    <span className="text-sm text-navy/70">{t(item.key)}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Right — form */}
          <div
            className={`transition-all duration-700 delay-200 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            {status === "success" ? (
              <div className="h-full flex flex-col items-start justify-center">
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <i className="ri-checkbox-circle-line text-gold text-5xl" />
                </div>
                <h3 className="text-xl font-bold text-navy mb-2">{t("contact.success.title")}</h3>
                <p className="text-navy/60 text-sm leading-relaxed">{t("contact.success.p")}</p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-6 text-sm text-gold font-medium hover:underline cursor-pointer"
                >
                  {t("contact.success.again")}
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="bg-cool-gray-50 rounded-lg p-8 space-y-5 border border-cool-gray-200"
              >
                <div>
                  <label className="block text-xs font-medium text-navy/60 uppercase tracking-wider mb-2" htmlFor="contact-name">
                    {t("contact.form.nameLabel")}
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder={t("contact.form.namePlaceholder")}
                    className="w-full px-4 py-3 text-sm text-navy bg-white border border-cool-gray-200 rounded-md focus:outline-none focus:border-navy transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-navy/60 uppercase tracking-wider mb-2" htmlFor="contact-email">
                    {t("contact.form.emailLabel")}
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder={t("contact.form.emailPlaceholder")}
                    className="w-full px-4 py-3 text-sm text-navy bg-white border border-cool-gray-200 rounded-md focus:outline-none focus:border-navy transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-navy/60 uppercase tracking-wider mb-2" htmlFor="contact-org">
                    {t("contact.form.orgLabel")}
                  </label>
                  <input
                    id="contact-org"
                    name="organization"
                    type="text"
                    placeholder={t("contact.form.orgPlaceholder")}
                    className="w-full px-4 py-3 text-sm text-navy bg-white border border-cool-gray-200 rounded-md focus:outline-none focus:border-navy transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-navy/60 uppercase tracking-wider mb-2" htmlFor="contact-message">
                    {t("contact.form.messageLabel")}{" "}
                    <span className="normal-case text-navy/40 tracking-normal">{t("contact.form.messageNote")}</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder={t("contact.form.messagePlaceholder")}
                    className="w-full px-4 py-3 text-sm text-navy bg-white border border-cool-gray-200 rounded-md focus:outline-none focus:border-navy transition-colors resize-none"
                  />
                </div>
                {status === "error" && (
                  <p className="text-red-500 text-xs">{t("contact.form.error")}</p>
                )}
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="w-full py-3.5 bg-navy text-white text-sm font-semibold rounded-md hover:bg-navy-light transition-colors duration-200 disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {status === "sending" ? t("contact.form.sending") : t("contact.form.submit")}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
